import React, { useEffect, useMemo, useState } from 'react';
import {
  Shield,
  Save,
  RefreshCw,
  Search,
  Users,
  Check,
  CheckSquare,
  Square,
  Lock,
  Unlock,
  KeyRound,
  X,
  Layers,
} from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import BlueHeader from '../components/BlueHeader';

const getInitials = (label = '') =>
  label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '?';

const FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'active', label: 'Activées' },
  { id: 'inactive', label: 'Désactivées' },
];

const Permissions = () => {
  const { user, checkAuth } = useAuth();
  const [profils, setProfils] = useState([]);
  const [selectedProfil, setSelectedProfil] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  const [categories, setCategories] = useState({});
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // affichage uniquement

  useEffect(() => {
    fetchProfils();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedProfil) {
      fetchPermissions(selectedProfil);
    }
  }, [selectedProfil]);

  const fetchProfils = async () => {
    try {
      const response = await http.get('/api/profils');
      const profilsData = Array.isArray(response.data) ? response.data : [];
      setProfils(profilsData);
      if (profilsData.length > 0) {
        setSelectedProfil(profilsData[0].idprofil);
      }
    } catch (error) {
      console.error('Erreur récupération profils:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await http.get('/api/permissions/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Erreur récupération catégories:', error);
    }
  };

  const fetchPermissions = async (profilId) => {
    try {
      setLoading(true);
      const response = await http.get(`/api/permissions/profil/${profilId}`);
      setPermissions(response.data);
    } catch (error) {
      console.error('Erreur récupération permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (permissionKey) => {
    setPermissions((prev) => ({
      ...prev,
      [permissionKey]: !prev[permissionKey],
    }));
  };



  const handleSelectAll = () => {
    const allPermissions = Object.values(categories).flatMap((cat) =>
      cat.permissions.map((p) => p.key)
    );
    setPermissions((prev) => {
      const updated = { ...prev };
      allPermissions.forEach((key) => {
        updated[key] = true;
      });
      return updated;
    });
  };

  const handleDeselectAll = () => {
    const allPermissions = Object.values(categories).flatMap((cat) =>
      cat.permissions.map((p) => p.key)
    );
    setPermissions((prev) => {
      const updated = { ...prev };
      allPermissions.forEach((key) => {
        updated[key] = false;
      });
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await http.put(`/api/permissions/profil/${selectedProfil}`, { permissions });
      alert('Permissions mises à jour avec succès');
      // Recharger les permissions du profil modifié
      await fetchPermissions(selectedProfil);
      // Si l'utilisateur connecté a le même profil, recharger ses permissions
      if (user?.idprofil === selectedProfil) {
        await checkAuth();
      }
    } catch (error) {
      console.error('Erreur mise à jour permissions:', error);
      alert('Erreur lors de la mise à jour des permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    fetchPermissions(selectedProfil);
  };

  const filteredProfiles = profils.filter((profil) =>
    profil.libprofil.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedProfile = profils.find((p) => p.idprofil === selectedProfil);

  /* ---------- Dérivés d'affichage (aucun impact fonctionnel) ---------- */
  const totalCount = Object.keys(categories).length;
  const activeCount = useMemo(
    () => Object.keys(categories).filter((k) => permissions[k]).length,
    [categories, permissions]
  );
  const percent = totalCount ? Math.round((activeCount / totalCount) * 100) : 0;

  const visibleCategories = Object.entries(categories)
    .filter(
      ([categoryKey, category]) =>
        category.label.toLowerCase().includes(permissionSearchTerm.toLowerCase()) ||
        categoryKey.toLowerCase().includes(permissionSearchTerm.toLowerCase())
    )
    .filter(([categoryKey]) => {
      if (statusFilter === 'active') return !!permissions[categoryKey];
      if (statusFilter === 'inactive') return !permissions[categoryKey];
      return true;
    });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900">
      <BlueHeader
        title="Gestion des Permissions"
        subtitle="Définir les droits d'accès par profil utilisateur"
        icon={Shield}
        showBack={false}
      />

      <div className="px-6 pb-10 pt-6">
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* ======================= SIDEBAR PROFILS ======================= */}
          <aside className="w-full flex-shrink-0 lg:w-80">
            <div className="sticky top-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="border-b border-slate-100 p-5 dark:border-gray-700">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0F1E3D] text-white">
                      <Users className="h-4 w-4" />
                    </div>
                    <div>
                      <h2 className="text-sm font-bold text-[#0F1E3D] dark:text-white">Profils</h2>
                      <p className="text-xs text-slate-500 dark:text-gray-400">
                        {profils.length} profil{profils.length > 1 ? 's' : ''} disponible
                        {profils.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Rechercher un profil..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-9 text-sm text-slate-800 placeholder-slate-400 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-900/40"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                      aria-label="Effacer la recherche"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="max-h-[calc(100vh-320px)] space-y-1 overflow-y-auto p-3">
                {filteredProfiles.length === 0 && (
                  <div className="px-4 py-10 text-center text-sm text-slate-400">
                    Aucun profil trouvé
                  </div>
                )}
                {filteredProfiles.map((profil) => {
                  const active = selectedProfil === profil.idprofil;
                  return (
                    <button
                      key={profil.idprofil}
                      type="button"
                      onClick={() => setSelectedProfil(profil.idprofil)}
                      className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all ${
                        active
                          ? 'bg-gradient-to-r from-[#0F1E3D] to-blue-700 text-white shadow-md shadow-blue-900/20'
                          : 'text-slate-700 hover:bg-slate-100 dark:text-gray-200 dark:hover:bg-gray-700/60'
                      }`}
                    >
                      <div
                        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-sm font-bold transition ${
                          active
                            ? 'bg-white/15 text-white ring-1 ring-white/30'
                            : 'bg-slate-100 text-slate-600 group-hover:bg-white dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {getInitials(profil.libprofil)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold">{profil.libprofil}</div>
                        <div
                          className={`text-xs ${
                            active ? 'text-blue-100' : 'text-slate-400 dark:text-gray-500'
                          }`}
                        >
                          {active ? 'Profil en cours d’édition' : 'Cliquer pour éditer'}
                        </div>
                      </div>
                      {active && (
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                          <Check className="h-4 w-4" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* ======================= ZONE PRINCIPALE ======================= */}
          <main className="min-w-0 flex-1">
            {!selectedProfile ? (
              <div className="flex h-72 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white text-slate-400 dark:border-gray-700 dark:bg-gray-800">
                <Shield className="mb-3 h-10 w-10" />
                <p className="text-sm font-medium">Sélectionnez un profil pour gérer ses permissions</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                {/* ---------- Bandeau profil + actions ---------- */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#0F1E3D] via-[#14306b] to-blue-700 px-6 py-6 text-white">
                  <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/5" />
                  <div className="pointer-events-none absolute -bottom-20 right-24 h-48 w-48 rounded-full bg-blue-400/10" />

                  <div className="relative flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 text-xl font-bold ring-1 ring-white/30 backdrop-blur">
                        {getInitials(selectedProfile.libprofil)}
                      </div>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-widest text-blue-200">
                          Profil sélectionné
                        </p>
                        <h3 className="text-2xl font-bold leading-tight">
                          {selectedProfile.libprofil}
                        </h3>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleRefresh}
                        className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-medium text-white ring-1 ring-white/25 backdrop-blur transition hover:bg-white/20"
                      >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Actualiser
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-[#0F1E3D] shadow-lg shadow-black/20 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Save className="h-4 w-4" />
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                    </div>
                  </div>

                  {/* Statistiques */}
                  <div className="relative mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur">
                      <div className="flex items-center gap-2 text-xs text-blue-100">
                        <Layers className="h-3.5 w-3.5" /> Modules
                      </div>
                      <div className="mt-1 text-2xl font-bold">{totalCount}</div>
                    </div>
                    <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur">
                      <div className="flex items-center gap-2 text-xs text-blue-100">
                        <Unlock className="h-3.5 w-3.5" /> Accès accordés
                      </div>
                      <div className="mt-1 text-2xl font-bold">{activeCount}</div>
                    </div>
                    <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur">
                      <div className="flex items-center justify-between text-xs text-blue-100">
                        <span className="flex items-center gap-2">
                          <KeyRound className="h-3.5 w-3.5" /> Couverture
                        </span>
                        <span className="font-semibold text-white">{percent}%</span>
                      </div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/15">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-emerald-300 to-emerald-400 transition-all duration-500"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ---------- Barre d'outils ---------- */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/70 px-6 py-4 dark:border-gray-700 dark:bg-gray-800/60">
                  <div className="flex flex-1 flex-wrap items-center gap-3">
                    <div className="relative w-full max-w-sm">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Rechercher une permission..."
                        value={permissionSearchTerm}
                        onChange={(e) => setPermissionSearchTerm(e.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-9 text-sm text-slate-800 placeholder-slate-400 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-900/40"
                      />
                      {permissionSearchTerm && (
                        <button
                          type="button"
                          onClick={() => setPermissionSearchTerm('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                          aria-label="Effacer la recherche"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>

                    <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 dark:border-gray-600 dark:bg-gray-700">
                      {FILTERS.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => setStatusFilter(f.id)}
                          className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                            statusFilter === f.id
                              ? 'bg-[#0F1E3D] text-white shadow'
                              : 'text-slate-500 hover:text-slate-800 dark:text-gray-300'
                          }`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleSelectAll}
                      className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                    >
                      <CheckSquare className="h-4 w-4" />
                      Tout cocher
                    </button>
                    <button
                      onClick={handleDeselectAll}
                      className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                    >
                      <Square className="h-4 w-4" />
                      Tout décocher
                    </button>
                  </div>
                </div>

                {/* ---------- Liste des permissions ---------- */}
                {loading ? (
                  <div className="grid grid-cols-1 gap-4 p-6 xl:grid-cols-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={i}
                        className="h-20 animate-pulse rounded-xl bg-slate-100 dark:bg-gray-700"
                      />
                    ))}
                  </div>
                ) : visibleCategories.length === 0 ? (
                  <div className="flex h-56 flex-col items-center justify-center text-slate-400">
                    <Search className="mb-3 h-8 w-8" />
                    <p className="text-sm font-medium">Aucune permission ne correspond</p>
                    <p className="text-xs">Modifiez votre recherche ou le filtre actif</p>
                  </div>
                ) : (
                  <div className="grid max-h-[calc(100vh-420px)] min-h-[240px] grid-cols-1 gap-4 overflow-y-auto p-6 xl:grid-cols-2">
                    {visibleCategories.map(([categoryKey, category]) => {
                      const isOn = !!permissions[categoryKey];
                      return (
                        <label
                          key={categoryKey}
                          htmlFor={`perm-${categoryKey}`}
                          className={`group relative flex cursor-pointer items-center gap-4 rounded-xl border p-4 transition-all ${
                            isOn
                              ? 'border-blue-300 bg-blue-50/60 shadow-sm dark:border-blue-700 dark:bg-blue-900/20'
                              : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800'
                          }`}
                        >
                          <span
                            className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full transition-colors ${
                              isOn ? 'bg-blue-600' : 'bg-transparent'
                            }`}
                          />
                          <div
                            className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl transition ${
                              isOn
                                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                                : 'bg-slate-100 text-slate-400 dark:bg-gray-700'
                            }`}
                          >
                            {isOn ? <Unlock className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                              {category.label}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <code className="truncate rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-500 dark:bg-gray-700 dark:text-gray-400">
                                {categoryKey}
                              </code>
                              {Array.isArray(category.permissions) && (
                                <span className="text-[11px] text-slate-400">
                                  {category.permissions.length} droit
                                  {category.permissions.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-1.5">
                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                isOn
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-slate-100 text-slate-500 dark:bg-gray-700 dark:text-gray-400'
                              }`}
                            >
                              {isOn ? 'Activé' : 'Désactivé'}
                            </span>
                            <span className="relative inline-flex items-center">
                              <input
                                id={`perm-${categoryKey}`}
                                type="checkbox"
                                checked={permissions[categoryKey] || false}
                                onChange={() => handleToggle(categoryKey)}
                                className="peer sr-only"
                              />
                              <span className="h-6 w-11 rounded-full bg-slate-300 transition-colors peer-checked:bg-blue-600 peer-focus-visible:ring-4 peer-focus-visible:ring-blue-200 dark:bg-gray-600 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-all after:content-[''] peer-checked:after:translate-x-5" />
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* ---------- Pied : résumé ---------- */}
                <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/70 px-6 py-3 text-xs text-slate-500 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-400">
                  <span>
                    {visibleCategories.length} permission{visibleCategories.length > 1 ? 's' : ''}{' '}
                    affichée{visibleCategories.length > 1 ? 's' : ''} sur {totalCount}
                  </span>
                  <span>Pensez à enregistrer pour appliquer vos modifications</span>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Permissions;